import type { ReactNode } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardHeaderActions,
  CardTitle,
} from '@/components/Card'
import { Button } from '@/components/Button'
import { formatAmount } from './format-amount'
import styles from './FinalReturnAmountsCard.module.css'

export interface FinalReturnAmountsYear {
  taxYear: string
  federalAmount?: number
  /**
   * `null` when the source is unknown.
   */
  federalSourceLabel: string | null
  stateAmounts: { state: string; amount: number; sourceLabel: string | null }[]
}

export interface FinalReturnAmountsCardProps {
  canEdit: boolean
  onEdit: () => unknown
  years: FinalReturnAmountsYear[]
  children?: ReactNode
}

export function FinalReturnAmountsCard({
  canEdit,
  onEdit,
  years,
  children,
}: FinalReturnAmountsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Final return amounts</CardTitle>
        {canEdit && (
          <CardHeaderActions>
            <Button
              label="Edit"
              variant="secondary"
              size="small"
              onPress={onEdit}
              testId="final-return-amounts-card-edit"
            />
          </CardHeaderActions>
        )}
      </CardHeader>

      <CardContent scroll>
        {years.map((year) => (
          <YearBlock key={year.taxYear} year={year} />
        ))}
      </CardContent>

      {children}
    </Card>
  )
}

interface YearBlockProps {
  year: FinalReturnAmountsYear
}

function YearBlock({ year }: YearBlockProps) {
  return (
    <div
      className={styles.yearBlock}
      data-test={`final-return-amounts-card-year-${year.taxYear}`}
    >
      <div className={styles.yearHeader}>
        <span className={styles.yearTitle}>Tax year {year.taxYear}</span>
      </div>

      {year.federalAmount !== undefined && (
        <AmountRow
          label="Federal"
          amount={year.federalAmount}
          sourceLabel={year.federalSourceLabel}
        />
      )}

      {year.stateAmounts.map((state) => (
        <AmountRow
          key={state.state}
          label={state.state}
          amount={state.amount}
          sourceLabel={state.sourceLabel}
        />
      ))}
    </div>
  )
}

interface AmountRowProps {
  label: string
  amount: number
  sourceLabel: string | null
}

function AmountRow({ label, amount, sourceLabel }: AmountRowProps) {
  return (
    <div className={styles.amountRow}>
      <div className={styles.amountLabelGroup}>
        <span className={styles.amountLabel}>{label}</span>
        {sourceLabel && (
          <span className={styles.sourceBadge}>{sourceLabel}</span>
        )}
      </div>
      <span className={styles.amountValue}>{formatAmount(amount)}</span>
    </div>
  )
}
